import React, { useState, useEffect, useRef } from 'react';
import socket from '../services/socket';
import { formatCurrency } from '../utils/formatters';
import { API_BASE_URL } from '../config';

const BottomTicker = () => {
  const [events, setEvents] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const tickerRef = useRef(null);

  // Fetch initial events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/events?limit=5`);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        const data = await response.json();
        setEvents(data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching events for ticker:', error);
      }
    };

    fetchEvents();
  }, []);

  // Fetch stock prices
  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/stocks`);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        const data = await response.json();
        setStocks(data);
      } catch (error) {
        console.error('Error fetching stocks for ticker:', error);
      }
    };

    fetchStocks();
    const interval = setInterval(fetchStocks, 10000);
    return () => clearInterval(interval);
  }, []);

  // Listen for new events
  useEffect(() => {
    const handleMarketEvent = (event) => {
      setEvents(prevEvents => {
        const newEvents = [event, ...prevEvents].slice(0, 5);
        return newEvents;
      });
    };

    socket.on('market-event', handleMarketEvent);
    return () => socket.off('market-event', handleMarketEvent);
  }, []);

  // Listen for price updates
  useEffect(() => {
    const handlePriceUpdate = (data) => {
      if (data && data.stocks) {
        setStocks(data.stocks);
      }
    };

    socket.on('price-update', handlePriceUpdate);
    return () => socket.off('price-update', handlePriceUpdate);
  }, []);

  // Filter events to only show those from the last 30 minutes
  const getRecentEvents = () => {
    const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
    return events.filter(event => {
      const eventTime = new Date(event.timestamp || event.created_at).getTime();
      return eventTime > thirtyMinutesAgo;
    });
  };

  // Humanize event type: "FDA_REJECTION" -> "FDA Rejection"
  const humanizeType = (type) => {
    if (!type) return 'Event';
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bFda\b/i, 'FDA').replace(/\bCeo\b/i, 'CEO').replace(/\bCfo\b/i, 'CFO');
  };

  const formatEventText = (event) => {
    // Get time ago
    const eventTime = new Date(event.timestamp || event.created_at);
    const minutesAgo = Math.floor((Date.now() - eventTime.getTime()) / 60000);
    const timeAgo = minutesAgo < 1 ? 'now' : `${minutesAgo}m ago`;

    // Build description
    let description = event.description || event.message || '';

    // If no description, build one from type
    if (!description) {
      const typeLabels = {
        'bull_run': 'Bull Run',
        'market_crash': 'Market Crash',
        'volatility': 'High Volatility',
        'MARKET_OPEN': 'Market Opened',
        'MARKET_CLOSE': 'Market Closed',
        'stock_split': 'Stock Split',
        'merger': 'Merger News',
        'earnings': 'Earnings Report',
        'custom': 'Market Event'
      };
      description = typeLabels[event.type] || humanizeType(event.type);
    }

    // Add percentage if available
    let change = '';
    if (event.changePercent !== undefined && event.changePercent !== null) {
      const sign = event.changePercent > 0 ? '+' : '';
      change = ` (${sign}${event.changePercent}%)`;
    } else if (event.multiplier && event.multiplier !== 1) {
      const percent = ((event.multiplier - 1) * 100).toFixed(1);
      const sign = percent > 0 ? '+' : '';
      change = ` (${sign}${percent}%)`;
    }

    // Add affected stocks - convert stock IDs to ticker symbols
    let stocksText = '';
    if (event.affectedStocks && event.affectedStocks !== 'ALL' && event.affectedStocks !== 'all') {
      let affectedList = event.affectedStocks;

      // Convert to array if it's a string
      if (typeof affectedList === 'string') {
        affectedList = affectedList.split(',').map(s => s.trim()).filter(Boolean);
      }

      // Now process as array
      if (Array.isArray(affectedList) && affectedList.length > 0) {
        const firstStock = affectedList[0];
        if (firstStock !== 'ALL' && (typeof firstStock !== 'object' || firstStock.ticker !== 'ALL')) {
          const tickers = affectedList.map(stockId => {
            // If it's an object with ticker property
            if (typeof stockId === 'object' && stockId.ticker) {
              return stockId.ticker;
            }
            // If it's already a ticker (not starting with 'stock-'), return as-is
            if (typeof stockId === 'string' && !stockId.startsWith('stock-')) {
              return stockId;
            }
            // Look up the ticker from the stocks array
            const stockObj = stocks.find(s => s.id === stockId);
            return stockObj ? stockObj.ticker : null;
          }).filter(Boolean).slice(0, 3).join(', ');
          if (tickers) stocksText = ` [${tickers}]`;
        }
      }
    }

    return `${timeAgo}: ${description}${change}${stocksText}`;
  };

  const formatStock = (stock) => {
    const change = stock.basePrice ? ((stock.currentPrice - stock.basePrice) / stock.basePrice) * 100 : 0;
    const changeSymbol = change >= 0 ? '+' : '';
    return {
      ticker: stock.ticker,
      price: formatCurrency(stock.currentPrice),
      change: `${changeSymbol}${change.toFixed(1)}%`,
      isPositive: change >= 0
    };
  };

  // Build combined ticker items - interleave stocks and events
  const buildTickerItems = () => {
    const items = [];
    const stockItems = stocks.map(stock => {
      const formatted = formatStock(stock);
      return {
        type: 'stock',
        text: `${formatted.ticker} ${formatted.price} ${formatted.change}`,
        isPositive: formatted.isPositive
      };
    });

    // Only show recent events (last 30 minutes)
    const recentEvents = getRecentEvents();
    const eventItems = recentEvents.map(event => {
      const isPositive = (event.changePercent > 0) || (event.multiplier && event.multiplier > 1);
      return {
        type: 'event',
        text: formatEventText(event),
        isPositive
      };
    });

    // Interleave: add an event after every 3 stocks
    let eventIndex = 0;
    stockItems.forEach((stock, i) => {
      items.push(stock);
      if ((i + 1) % 3 === 0 && eventIndex < eventItems.length) {
        items.push(eventItems[eventIndex]);
        eventIndex++;
      }
    });

    // Add any remaining events at the end
    while (eventIndex < eventItems.length) {
      items.push(eventItems[eventIndex]);
      eventIndex++;
    }

    return items;
  };

  const tickerItems = buildTickerItems();

  return (
    <div
      className="bottom-ticker"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="ticker-live-label">LIVE</div>
      <div className="ticker-scroll-container">
        <div
          ref={tickerRef}
          className={`ticker-scroll-content ${isPaused ? 'paused' : ''}`}
        >
          {/* Duplicate for seamless loop */}
          {[...tickerItems, ...tickerItems].map((item, index) => (
            <span
              key={index}
              className={`ticker-scroll-item ${item.isPositive ? 'positive' : 'negative'} ${item.type}`}
            >
              {item.text}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BottomTicker;
